'use client';

import { useLayoutEffect, useState, type CSSProperties } from 'react';

export type AnchoredFixedPanelPosition = Pick<CSSProperties, 'top' | 'left' | 'width'>;

function measureAnchoredPanelPosition(anchor: HTMLElement): AnchoredFixedPanelPosition {
    const { bottom } = anchor.getBoundingClientRect();

    return {
        top: bottom,
        left: 0,
        width: document.documentElement.clientWidth,
    };
}

/** Keeps a fixed panel aligned below an anchor while the sticky header scrolls or resizes. */
export function useAnchoredFixedPanelPosition(
    open: boolean,
    anchorElement: HTMLElement | null
): AnchoredFixedPanelPosition | null {
    const [position, setPosition] = useState<AnchoredFixedPanelPosition | null>(() =>
        open && anchorElement ? measureAnchoredPanelPosition(anchorElement) : null
    );

    useLayoutEffect(() => {
        if (!open || !anchorElement) {
            setPosition(null);
            return;
        }

        let rafId: number | null = null;

        const commitPosition = () => {
            setPosition(measureAnchoredPanelPosition(anchorElement));
        };

        const scheduleUpdate = () => {
            if (rafId !== null) {
                return;
            }

            rafId = requestAnimationFrame(() => {
                rafId = null;
                commitPosition();
            });
        };

        commitPosition();
        window.addEventListener('resize', scheduleUpdate);
        window.addEventListener('scroll', scheduleUpdate, { capture: true, passive: true });

        return () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
            window.removeEventListener('resize', scheduleUpdate);
            window.removeEventListener('scroll', scheduleUpdate, true);
        };
    }, [open, anchorElement]);

    return position;
}
